import { LinkButton } from '@euler/app/components/LinkButton';
import { useMemberPickerBottomSheetContext } from '@euler/app/components/MemberPickerBottomSheet';
import { Typography } from '@euler/app/components/typography';
import { useServiceSubjectsHost } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/host';
import { serviceSubjectEquals } from '@euler/app/flows/order/util/serviceSubjectEquals';
import { sortedServiceSubjectsByType } from '@euler/app/flows/order/util/sortedServiceSubjectsByType';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { isFaultLikeServiceSubject } from '@euler/functions/isFaultLikeServiceSubject';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetHeight } from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { FaultLikeServiceSubject, ServiceSubject } from '@euler/model';
import { OrgUserRoleType, ServiceSubjectType } from '@euler/model/enum';
import { showAlert, withConfirmation } from '@euler/utils';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import BottomSheet, { BottomSheetFlatList } from '@gorhom/bottom-sheet';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  FlatList,
  Platform,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject, Subject } from 'rxjs';
import { useTheme } from 'styled-components';
import { kSubjectTypeInfoMap } from '../functions/subjectTypeInfoUtil';
import { useSelectedServiceSubjectsBottomSheetFooterHeight } from '../functions/useSelectedServiceSubjectsBottomSheetFooterHeight';
import { useServiceSubjectsContext } from './ServiceSubjectsContext';

const kCellHeight = 60;

export type SelectedServiceSubjectsBottomSheetAction = {
  type: 'reveal';
  index?: number;
};

export const SelectedServiceSubjectsBottomSheet = memo(
  ({
    action$,
    onDone,
  }: {
    action$: Subject<SelectedServiceSubjectsBottomSheetAction>;
    onDone?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { navBarHeight } = useSystemMetrics();
    const height = useGeneralBottomSheetHeight();
    const footerHeight = useSelectedServiceSubjectsBottomSheetFooterHeight();
    const bottomSheetRef = useRef<BottomSheet>(null);
    const minSnapPoint = footerHeight;
    const lowerWatermarkSnapPoint = kCellHeight * 5 + minSnapPoint;
    const mediumWatermarkSnapPoint = kCellHeight * 10 + minSnapPoint;
    const maxSnapPoint = height - navBarHeight;
    const snapPoints = useMemo(
      () => [
        minSnapPoint,
        lowerWatermarkSnapPoint,
        mediumWatermarkSnapPoint,
        maxSnapPoint,
      ],
      [
        lowerWatermarkSnapPoint,
        maxSnapPoint,
        mediumWatermarkSnapPoint,
        minSnapPoint,
      ],
    );

    const {
      orderDetail$,
      updateDetail,
      capabilities,
      showCustomServiceSubjectModal$,
    } = useServiceSubjectsHost();
    const [{ serviceSubjects = [] }] = useBehaviorSubject(orderDetail$);
    const { removeServiceSubject } = useServiceSubjectsContext();
    const { memberPickerPresentationRequest$ } =
      useMemberPickerBottomSheetContext();

    const checked$ = useCreation(
      () => new BehaviorSubject<ServiceSubject[]>([]),
      [],
    );
    const [checked, setChecked] = useBehaviorSubject(checked$);

    const isChecked = (subject: ServiceSubject) =>
      checked.findIndex(x => serviceSubjectEquals(x, subject)) >= 0;

    const isSubjectReadonly = (item: ServiceSubject) => {
      return (
        (item.type === ServiceSubjectType.Inspection &&
          capabilities?.readonlyInspectionSubjects.has(item.name)) ||
        (item.type === ServiceSubjectType.Construction &&
          capabilities?.readonlyConstructionSubjects.has(item.name))
      );
    };

    const availableServiceSubjects = serviceSubjects.filter(
      x => !isSubjectReadonly(x),
    );

    const isAllChecked =
      checked.length > 0 && checked.length === availableServiceSubjects.length;

    const hasUnassignedItems = availableServiceSubjects.some(
      x => !x.assignedTo,
    );

    const onItemPress = usePersistFn((subject: ServiceSubject) => {
      const index = checked.findIndex(x => serviceSubjectEquals(x, subject));
      if (index >= 0) {
        setChecked(l => l.filter(x => !serviceSubjectEquals(x, subject)));
      } else {
        setChecked(l => [...l, subject]);
      }
    });

    const onEditItem = usePersistFn((item: FaultLikeServiceSubject) => {
      showCustomServiceSubjectModal$.next(item);
    });

    const onRemoveItem = usePersistFn((subject: ServiceSubject) => {
      if (isSubjectReadonly(subject)) {
        const name =
          subject.type === ServiceSubjectType.Inspection
            ? '检测任务'
            : '施工项目';
        const msg: string[] = [
          name,
          `"${subject.name}"`,
          `已经开始或已完成, 若要取消该${name}, 请前往${name}页面完成相关操作!`,
        ];
        void showAlert('提示', msg.join(''));
        return;
      }
      const index = serviceSubjects.findIndex(x =>
        serviceSubjectEquals(x, subject),
      );
      if (index >= 0) {
        removeServiceSubject(index);
      }
      setChecked(
        checked.filter(x => x.type !== subject.type || x.name !== subject.name),
      );
    });

    const onSelectedButtonPress = useCallback(() => {
      bottomSheetRef.current?.snapToIndex(2);
    }, []);

    const onToggleSelectAll = usePersistFn(() => {
      if (isAllChecked) {
        setChecked([]);
      } else {
        setChecked(availableServiceSubjects);
      }
    });

    const onClear = usePersistFn(async () => {
      if (
        await withConfirmation({
          title: '确认',
          message: '是否确认清空全部服务项目?',
        })
      ) {
        const list = serviceSubjects.filter(isSubjectReadonly);
        setChecked(list);
        updateDetail(draft => {
          draft.serviceSubjects = list;
        });
      }
    });

    const animatedIndex = useSharedValue(0);
    const animatedPosition = useSharedValue(0);
    const animatedEmptyStyle = useAnimatedStyle(() => {
      const visibleHeight = interpolate(
        animatedPosition.value,
        [
          height - maxSnapPoint,
          height - mediumWatermarkSnapPoint,
          height - lowerWatermarkSnapPoint,
          height - minSnapPoint,
        ],
        [
          maxSnapPoint - minSnapPoint,
          mediumWatermarkSnapPoint - minSnapPoint,
          lowerWatermarkSnapPoint - minSnapPoint,
          0,
        ],
      );
      return {
        opacity: interpolate(
          animatedPosition.value,
          [
            height - maxSnapPoint,
            height - mediumWatermarkSnapPoint,
            height - lowerWatermarkSnapPoint,
            height - minSnapPoint,
          ],
          [1, 1, 1, 0],
        ),
        transform: [
          {
            translateY: visibleHeight / 2 - 100,
          },
          {
            scale: interpolate(
              animatedPosition.value,
              [
                height - maxSnapPoint,
                height - mediumWatermarkSnapPoint,
                height - lowerWatermarkSnapPoint,
                height - minSnapPoint,
              ],
              [1, 1, 1, 0],
            ),
          },
        ],
      };
    });

    useEffect(() => {
      const sub = action$.subscribe(action => {
        if (action.type === 'reveal') {
          bottomSheetRef.current?.snapToIndex(action.index ?? 2);
        }
      });
      return () => sub.unsubscribe();
    }, [action$]);

    const sortedServiceSubjects = useMemo(
      () => sortedServiceSubjectsByType(serviceSubjects),
      [serviceSubjects],
    );

    const onAssignButtonPress = usePersistFn(() => {
      memberPickerPresentationRequest$.next({
        title: '项目派工',
        role: checked.every(x => x.type === ServiceSubjectType.Inspection)
          ? OrgUserRoleType.ServiceAgents
          : checked.every(x => x.type !== ServiceSubjectType.Inspection)
          ? OrgUserRoleType.Technicians
          : undefined,
        onSelect: member => {
          memberPickerPresentationRequest$.next(undefined);
          setChecked([]);
          updateDetail(draft => {
            draft.serviceSubjects?.forEach(subject => {
              if (checked.find(x => serviceSubjectEquals(x, subject))) {
                subject.assignedTo = { id: member.id, name: member.name };
              }
            });
          });
        },
      });
    });
    // BottomSheet specific scroll view components do not work on Web right now.
    // see https://github.com/gorhom/react-native-bottom-sheet/issues/11.
    const ListComponent =
      Platform.OS === 'web' ? FlatList : BottomSheetFlatList;

    return (
      <>
        <BottomSheet
          ref={bottomSheetRef}
          snapPoints={snapPoints}
          animatedIndex={animatedIndex}
          animatedPosition={animatedPosition}
          index={0}
          css={`
            background-color: #fff;
            box-shadow: 0 -1px ${rpx(15)}px rgba(0, 0, 0, 0.5);
            elevation: 3;
          `}
          style={{
            elevation: 3,
          }}
        >
          <ListComponent
            data={sortedServiceSubjects}
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: footerHeight,
            }}
            ListEmptyComponent={
              <Animated.View
                css={`
                  height: ${rpx(100)}px;
                `}
                style={animatedEmptyStyle}
              >
                <EmptyView
                  message="尚未添加任何服务项目"
                  messageStyle={{
                    fontFamily: FontFamily.NotoSans.Medium,
                    fontSize: rpx(15),
                  }}
                />
              </Animated.View>
            }
            ListHeaderComponent={
              serviceSubjects.length ? (
                <HeaderComponent
                  hasAvailableServiceSubjects={
                    availableServiceSubjects.length > 0
                  }
                  isAllChecked={isAllChecked}
                  onToggleSelectAll={onToggleSelectAll}
                  onClear={onClear}
                />
              ) : undefined
            }
            renderItem={({ item }) => (
              <Cell
                selected={isChecked(item)}
                subject={item}
                readonly={isSubjectReadonly(item)}
                onPress={onItemPress}
                onRemove={onRemoveItem}
                onEdit={onEditItem}
              />
            )}
          />
        </BottomSheet>
        <BottomBar
          hasUnassignedItems={hasUnassignedItems}
          count={serviceSubjects.length}
          checkedCount={checked.length}
          onSelectedButtonPress={onSelectedButtonPress}
          onDoneButtonPress={onDone}
          onAssignButtonPress={onAssignButtonPress}
        />
      </>
    );
  },
);

const HeaderComponent = memo(
  ({
    hasAvailableServiceSubjects,
    isAllChecked,
    onToggleSelectAll,
    onClear,
  }: {
    hasAvailableServiceSubjects: boolean;
    isAllChecked: boolean;
    onToggleSelectAll: () => void;
    onClear: () => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    return (
      <HStack
        justifyContent="flex-start"
        css={`
          padding: 0 ${rpx(17.5)}px;
          margin-bottom: ${rpx(15)}px;
        `}
      >
        <Icon name="information-solid" size={rpx(16)} color={theme.link} />
        <Typography.Label
          bold
          color="#777"
          size={rpx(16)}
          lineHeight={rpx(20)}
          css={`
            margin-left: ${rpx(4)}px;
            flex: 1;
          `}
        >
          可选择项目进行派工
        </Typography.Label>
        {hasAvailableServiceSubjects && (
          <LinkButton
            text={isAllChecked ? '取消全选' : '全选'}
            onPress={onToggleSelectAll}
          />
        )}
        {hasAvailableServiceSubjects && (
          <LinkButton
            text={'清空'}
            onPress={onClear}
            css={`
              margin-left: ${rpx(8)}px;
            `}
          />
        )}
      </HStack>
    );
  },
);

const BottomBar = memo(
  ({
    count,
    checkedCount,
    hasUnassignedItems,
    onSelectedButtonPress,
    onDoneButtonPress,
    onAssignButtonPress,
  }: {
    count: number;
    checkedCount: number;
    hasUnassignedItems: boolean;
    onSelectedButtonPress?: () => void;
    onDoneButtonPress?: () => void;
    onAssignButtonPress?: () => void;
  }) => {
    const insets = useSafeAreaInsets();
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const paddingTop = rpx(10);
    const paddingBottom = Math.max(rpx(10), insets.bottom);
    const minHeight = rpx(44) + paddingTop + paddingBottom;

    return (
      <View
        css={`
          padding-top: ${paddingTop}px;
          padding-bottom: ${paddingBottom}px;
          min-height: ${minHeight}px;
          background-color: #fff;
          elevation: 1;
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
        `}
      >
        <HStack
          css={`
            padding: 0 ${rpx(15)}px;
            flex: 1;
          `}
        >
          <TouchableOpacity
            onPress={onSelectedButtonPress}
            css={`
              background-color: #ccc;
              height: ${rpx(44)}px;
              border-radius: ${rpx(22)}px;
              padding: 0 ${rpx(22)}px;
              align-items: center;
              justify-content: center;
              flex-direction: row;
            `}
          >
            <Icon name="clipboard" size={rpx(16)} color={'#333'} />
            <Typography.Label color={'#333'} size={rpx(16)}>
              {`已加入项目 (${count})`}
            </Typography.Label>
          </TouchableOpacity>
          {checkedCount > 0 && (
            <TouchableOpacity
              onPress={onAssignButtonPress}
              css={`
                flex: 1;
                margin-left: ${rpx(10)}px;
                background-color: #2e3191;
                height: ${rpx(44)}px;
                border-radius: ${rpx(22)}px;
                align-items: center;
                justify-content: center;
              `}
            >
              <Typography.Label size={rpx(20)} color="#fff" regular>
                派工
              </Typography.Label>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={onDoneButtonPress}
            css={`
              flex: 1;
              margin-left: ${rpx(10)}px;
              background-color: ${checkedCount && hasUnassignedItems
                ? StatusColors.Danger
                : theme.link};
              height: ${rpx(44)}px;
              border-radius: ${rpx(22)}px;
              align-items: center;
              justify-content: center;
            `}
          >
            <Typography.Label size={rpx(20)} color="#fff" regular>
              完成
            </Typography.Label>
          </TouchableOpacity>
        </HStack>
      </View>
    );
  },
);

const Cell = memo(
  ({
    subject: item,
    selected,
    readonly,
    onEdit,
    onPress,
    onRemove,
    style,
  }: {
    subject: ServiceSubject;
    selected: boolean;
    readonly?: boolean;
    onEdit?: (item: FaultLikeServiceSubject) => void;
    onPress: (item: ServiceSubject) => void;
    onRemove: (item: ServiceSubject) => void;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const subjectTypeInfo = kSubjectTypeInfoMap[item.type];
    const {
      construction: {
        schedule: {
          workItem: {
            cell: { textLight, bgLight },
          },
        },
      },
    } = useTheme();

    const onCellPress = useCallback(() => {
      onPress?.(item);
    }, [item, onPress]);

    const onRemovePress = useCallback(() => {
      onRemove?.(item);
    }, [item, onRemove]);

    const onEditPress = useCallback(() => {
      if (!isFaultLikeServiceSubject(item)) return;
      onEdit?.(item);
    }, [item, onEdit]);

    return (
      <TouchableOpacity
        onPress={readonly ? undefined : onCellPress}
        activeOpacity={readonly ? 1 : undefined}
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          min-height: ${rpx(kCellHeight)}px;
          background-color: ${bgLight};
          padding-left: ${rpx(15)}px;
          padding-right: ${rpx(8)}px;
          padding-top: ${rpx(5)}px;
          padding-bottom: ${rpx(5)}px;
          margin: 0;
          border-bottom-width: 1px;
          border-bottom-color: #eee;
          border-radius: 0;
        `}
        style={style}
      >
        <Icon
          name={selected ? 'checkbox' : 'checkbox_outline'}
          color={
            readonly ? '#ddd' : selected ? 'rgba(32, 127, 231, 1)' : '#777'
          }
          size={rpx(20)}
          css={`
            margin-right: ${rpx(5)}px;
            align-self: flex-start;
            transform: translateY(${rpx(3.3)}px);
          `}
        />
        <View
          css={`
            flex: 1;
          `}
        >
          <HStack justifyContent="flex-start" alignItems="center">
            <HStack
              justifyContent="flex-start"
              alignItems="center"
              style={{ flex: 1, marginRight: rpx(10) }}
            >
              <Typography.Label
                medium
                size={rpx(15)}
                numberOfLines={0}
                color={
                  readonly
                    ? '#ccc'
                    : item.assignedTo
                    ? textLight
                    : StatusColors.Danger
                }
                lineHeight={rpx(20)}
              >
                {item.name}
              </Typography.Label>
              {isFaultLikeServiceSubject(item) && (
                <Center.Touchable
                  onPress={onEditPress}
                  style={{ marginLeft: rpx(5) }}
                >
                  <Icon
                    name="note"
                    color="rgba(32, 127, 231, 1)"
                    size={rpx(16)}
                  />
                </Center.Touchable>
              )}
            </HStack>

            <Center
              css={`
                border-radius: ${rpx(3)}px;
                height: ${rpx(20)}px;
                padding: 0 ${rpx(5)}px;
                background-color: ${subjectTypeInfo.bgColor};
                margin-left: ${rpx(5)}px;
                flex-shrink: 0;
                flex-grow: 0;
              `}
            >
              <Typography.Label
                color={subjectTypeInfo.textColor}
                light
                size={rpx(10)}
                lineHeight={rpx(20)}
              >
                {subjectTypeInfo.text}
              </Typography.Label>
            </Center>
            <TouchableOpacity
              onPress={onRemovePress}
              css={`
                width: ${rpx(50)}px;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
              `}
              hitSlop={PredefinedHitSlops.large}
            >
              <Icon
                name={'minus-circle'}
                color={readonly ? '#ccc' : StatusColors.Danger}
                size={rpx(24)}
              />
            </TouchableOpacity>
          </HStack>

          <HStack justifyContent="flex-start">
            <Typography.Label
              size={rpx(5)}
              lineHeight={rpx(20)}
              css={`
                margin-right: ${rpx(4)}px;
              `}
            >
              {item.assignedTo ? '🟢' : '🟠'}
            </Typography.Label>
            <Typography.Label
              light
              size={rpx(13)}
              lineHeight={rpx(20)}
              color={readonly ? '#ccc' : '#666'}
            >
              {item.type === ServiceSubjectType.Inspection
                ? '检测技师: '
                : '施工技师: '}
            </Typography.Label>
            <Typography.Label
              light
              size={rpx(13)}
              lineHeight={rpx(20)}
              color={
                readonly
                  ? '#ccc'
                  : item.assignedTo
                  ? '#666'
                  : StatusColors.Danger
              }
            >
              {item.assignedTo?.name ?? '未派工'}
            </Typography.Label>
          </HStack>

          {isFaultLikeServiceSubject(item) && item.note?.trim() ? (
            <Typography.Text
              size={rpx(10)}
              color="#777"
              lineHeight={rpx(16)}
              light
              mr={rpx(110)}
            >
              维修备注: {item.note}
            </Typography.Text>
          ) : null}
        </View>
      </TouchableOpacity>
    );
  },
);
